import {
  Alert,
  Autocomplete,
  Button,
  Card,
  CardContent,
  Chip,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  Grid,
  IconButton,
  MenuItem,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material'
import { Dialog } from '@/components/AppDialog'
import { TamilNameField } from '@/components/TamilNameField'
import AddIcon from '@mui/icons-material/Add'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import { useEffect, useMemo, useState } from 'react'
import {
  useCreatePackage,
  useHalls,
  useServiceTypes,
  useSessionTypes,
  useOutlets,
  useSavePackageVersion,
} from '@/api/hooks'
import { ErrorBanner, formatMoney } from '@/components/common'
import { DishPicker, useDishMemory, type Dish } from './DishPicker'
import { PortionList } from './PortionList'
import { WHOLE_PORTION } from './portions'

/** One numbered item on the card and the dishes it offers. */
type ItemDraft = {
  itemLabel: string
  /** How many of the choices a quotation takes. The minimum follows it. */
  maxSelections: string
  extraItemPrice: string
  allowExtra: boolean
  menuItemIds: string[]
  mandatoryIds: string[]
  /** Serving fraction per chosen dish, keyed by menu item id. */
  portions: Record<string, number>
  /** A note about that dish on this package, keyed the same way. */
  remarks: Record<string, string>
}

const BLANK_ITEM: ItemDraft = {
  itemLabel: '',
  maxSelections: '1',
  extraItemPrice: '0',
  allowExtra: false,
  menuItemIds: [],
  mandatoryIds: [],
  portions: {},
  remarks: {},
}

/**
 * Creates a package and its first version in one pass: what it is called, where
 * and when it is sold, what it costs, and the numbered items the customer
 * chooses from — Item 1, Item 2, up to as many as the package needs.
 *
 * <p>Dishes are picked from the whole catalogue rather than one category, so an
 * item can offer whatever the card actually offers.</p>
 *
 * The version is left as a draft so it can be reviewed before it is published
 * and becomes quotable.
 */
export function PackageBuilderDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { data: outlets } = useOutlets()
  const createPackage = useCreatePackage()
  const saveVersion = useSavePackageVersion()

  const [code, setCode] = useState('')
  const [name, setName] = useState('')
  const [nameLocal, setNameLocal] = useState('')
  // A package built once and offered under several lines of business, same
  // menu and rate throughout, rather than copied once per service.
  const [serviceTypes, setServiceTypes] = useState<string[]>(['BANQUET'])
  const [sessionType, setSessionType] = useState('ANY')
  const [outletId, setOutletId] = useState('')
  // What this outlet actually sells, from the service master. A package for a
  // service the outlet does not offer is a package nobody can quote.
  const { data: availableServiceTypes } = useServiceTypes({ outletId: outletId || undefined })
  const services = (availableServiceTypes ?? []).filter((type) => type.bookable)
  const selectedServices = services.filter((type) => serviceTypes.includes(type.code))
  // A banquet or a restaurant party books a space of ours; an outdoor job or a
  // takeaway has none. Offered under several services at once, it books one
  // the moment any of them does.
  const booksASpace = selectedServices.some((type) => Boolean(type.spaceType))
  const spaceTypesInUse = new Set(
    selectedServices.filter((type) => type.spaceType).map((type) => type.spaceType),
  )
  // Halls and dining rooms are both spaces, but different ones. Offered under
  // only one kind at once, the picker narrows to it; offered under both, it
  // shows every space rather than guessing which was meant.
  const spaceType = spaceTypesInUse.size === 1
    ? ((spaceTypesInUse.values().next().value === 'DINING' ? 'DINING' : 'HALL') as 'HALL' | 'DINING')
    : undefined
  const { data: halls } = useHalls(outletId || undefined, booksASpace ? spaceType : undefined)

  // Moving to an outlet that does not sell what was chosen would leave the
  // field naming a service the outlet cannot deliver.
  useEffect(() => {
    if (services.length === 0) return
    const stillOffered = serviceTypes.filter((code) => services.some((type) => type.code === code))
    if (stillOffered.length === 0) {
      setServiceTypes([services[0].code])
    } else if (stillOffered.length !== serviceTypes.length) {
      setServiceTypes(stillOffered)
    }
  }, [services, serviceTypes])

  // The sittings this line of business is sold by, off the master. Four were
  // written in here, so a package for a midnight buffet had nothing to be sold
  // under. Offered under more than one service, every sitting shows rather
  // than narrowing to just one service's.
  const { data: sessionTypes } = useSessionTypes({
    serviceType: serviceTypes.length === 1 ? serviceTypes[0] : undefined,
  })
  const sittings = sessionTypes ?? []

  // A sitting the new service is not assigned would leave the field naming a
  // meal that service never sells; "any" is always true of it.
  useEffect(() => {
    if (sessionTypes === undefined || sessionType === 'ANY') return
    if (!sittings.some((sitting) => sitting.code === sessionType)) {
      setSessionType('ANY')
    }
  }, [sessionTypes, sittings, sessionType])

  const [hallIds, setHallIds] = useState<string[]>([])
  const [basePricePerPerson, setBasePrice] = useState('')
  const [taxRatePercent, setTaxRate] = useState('5')
  // Whether the per-head price above already has that GST in it. "Four hundred
  // a head, all in" and "four hundred plus tax" are different numbers, and the
  // customer was told one of them.
  const [pricing, setPricing] = useState<'DEFAULT' | 'INCLUSIVE' | 'EXCLUSIVE'>('DEFAULT')
  const priceInclusiveTax =
    pricing === 'DEFAULT' ? undefined : pricing === 'INCLUSIVE'
  const [items, setItems] = useState<ItemDraft[]>([{ ...BLANK_ITEM }])

  // Every dish the pickers have handed back, so a choice made under one search
  // still resolves after the next search has replaced the results.
  const { known, remember } = useDishMemory()

  const usableItems = useMemo(
    () => items.filter((item) => item.menuItemIds.length > 0),
    [items],
  )
  const valid = Boolean(code.trim() && name.trim() && basePricePerPerson)
    && serviceTypes.length > 0 && usableItems.length > 0

  const updateItem = (index: number, patch: Partial<ItemDraft>) =>
    setItems((current) => current.map((item, position) => (position === index ? { ...item, ...patch } : item)))

  const submit = () => {
    createPackage.mutate(
      {
        code: code.trim().toUpperCase(),
        name: name.trim(),
        nameLocal: nameLocal.trim() || undefined,
        serviceTypes,
        sessionType,
        outletId: outletId || undefined,
        hallIds: booksASpace ? hallIds : undefined,
        basePricePerPerson: Number(basePricePerPerson),
        taxRatePercent: Number(taxRatePercent || 0),
        priceInclusiveTax,
      },
      {
        onSuccess: (created) => {
          // Creating the package seeds version 1; fill it with the items.
          const pkg = created as { id: string; currentVersionId?: string }
          saveVersion.mutate(
            {
              packageId: pkg.id,
              versionId: pkg.currentVersionId,
              body: {
                // The guarantee, child percentage and complimentary guests are
                // no longer set here; the version keeps the defaults it was
                // created with.
                basePricePerPerson: Number(basePricePerPerson),
                taxRatePercent: Number(taxRatePercent || 0),
                priceInclusiveTax,
                items: usableItems.map((item, index) => ({
                  itemNumber: index + 1,
                  itemLabel: item.itemLabel.trim() || `Item ${index + 1}`,
                  // One number: an item is taken exactly this many times.
                  minSelections: Number(item.maxSelections || 0),
                  maxSelections: Number(item.maxSelections || 0),
                  extraItemPrice: Number(item.extraItemPrice || 0),
                  allowExtra: item.allowExtra,
                  choices: item.menuItemIds.map((menuItemId) => ({
                    menuItemId,
                    mandatory: item.mandatoryIds.includes(menuItemId),
                    portion: item.portions[menuItemId] ?? WHOLE_PORTION,
                    remarks: item.remarks[menuItemId] || undefined,
                  })),
                })),
              },
            },
            { onSuccess: onClose },
          )
        },
      },
    )
  }

  const busy = createPackage.isPending || saveVersion.isPending

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>New package</DialogTitle>
      <DialogContent>
        <ErrorBanner error={createPackage.error ?? saveVersion.error} />
        <Alert severity="info" sx={{ mb: 2 }}>
          The package is created as a <strong>draft version</strong>. Publish it from the package list
          before it can be quoted — that way a half-built package never reaches a customer.
        </Alert>

        <Typography variant="subtitle2" sx={{ mt: 1, mb: 1 }}>
          Package
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              required
              label="Code"
              value={code}
              onChange={(event) => setCode(event.target.value.toUpperCase())}
              placeholder="PKG_PREM_VEG"
            />
          </Grid>
          <Grid item xs={12} md={5}>
            <TextField
              fullWidth
              required
              label="Name"
              value={name}
              onChange={(event) => setName(event.target.value)}
              placeholder="Premium vegetarian wedding package"
            />
          </Grid>
          {/* The heading on a menu sheet printed in Tamil. Left empty, the
              sheet prints the name above. */}
          <Grid item xs={12} md={3}>
            <TamilNameField
              value={nameLocal}
              onChange={setNameLocal}
              helperText="The heading on a Tamil menu sheet"
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              select
              fullWidth
              label="Outlet"
              value={outletId}
              onChange={(event) => setOutletId(event.target.value)}
              helperText="Leave blank to offer it at every outlet"
            >
              <MenuItem value="">All outlets</MenuItem>
              {(outlets ?? []).map((outlet) => (
                <MenuItem key={outlet.id} value={outlet.id}>
                  {outlet.name}
                </MenuItem>
              ))}
            </TextField>
          </Grid>

          <Grid item xs={12} md={6}>
            <Autocomplete
              multiple
              fullWidth
              options={services.map((type) => type.code)}
              value={serviceTypes.filter((code) => services.some((type) => type.code === code))}
              onChange={(_event, value) => setServiceTypes(value)}
              getOptionLabel={(code) => services.find((type) => type.code === code)?.name ?? code}
              renderTags={(value, getTagProps) =>
                value.map((code, index) => (
                  <Chip size="small" label={services.find((type) => type.code === code)?.name ?? code}
                        {...getTagProps({ index })} key={code} />
                ))
              }
              renderInput={(params) => (
                <TextField {...params} required={serviceTypes.length === 0} label="Service" />
              )}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              select
              fullWidth
              label="Session"
              value={sessionType}
              onChange={(event) => setSessionType(event.target.value)}
              helperText="The session this package is sold for"
            >
              {/* Any is the package saying it does not care which sitting;
                  the rest come off the master for this line of business. */}
              <MenuItem value="ANY">Any session</MenuItem>
              {sittings.map((sitting) => (
                <MenuItem key={sitting.code} value={sitting.code}>
                  {sitting.name}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          {/* A banquet or a restaurant party is sold against a space of ours;
              the other services have none. */}
          {booksASpace && (
            <Grid item xs={12} md={6}>
              <Autocomplete
                multiple
                fullWidth
                options={(halls ?? []).map((hall) => hall.id)}
                value={hallIds.filter((id) => (halls ?? []).some((hall) => hall.id === id))}
                onChange={(_event, value) => setHallIds(value)}
                getOptionLabel={(id) => halls?.find((hall) => hall.id === id)?.name ?? id}
                renderTags={(value, getTagProps) =>
                  value.map((id, index) => (
                    <Chip size="small" label={halls?.find((hall) => hall.id === id)?.name ?? id}
                          {...getTagProps({ index })} key={id} />
                  ))
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={spaceType === 'DINING' ? 'Dining rooms' : spaceType === 'HALL' ? 'Halls' : 'Spaces'}
                    helperText="Leave blank to offer it everywhere"
                  />
                )}
              />
            </Grid>
          )}
        </Grid>

        <Divider sx={{ my: 2 }} />
        <Typography variant="subtitle2" sx={{ mb: 1 }}>
          Pricing
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={6} md={3}>
            <TextField
              fullWidth
              required
              type="number"
              label="Per person"
              value={basePricePerPerson}
              onChange={(event) => setBasePrice(event.target.value)}
            />
          </Grid>
          <Grid item xs={6} md={3}>
            <TextField
              fullWidth
              type="number"
              label="GST %"
              value={taxRatePercent}
              onChange={(event) => setTaxRate(event.target.value)}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <TextField
              select
              fullWidth
              label="Price is"
              value={pricing}
              onChange={(event) =>
                setPricing(event.target.value as 'DEFAULT' | 'INCLUSIVE' | 'EXCLUSIVE')
              }
            >
              <MenuItem value="DEFAULT">As the company sets</MenuItem>
              <MenuItem value="EXCLUSIVE">Excluding GST</MenuItem>
              <MenuItem value="INCLUSIVE">Including GST</MenuItem>
            </TextField>
          </Grid>
        </Grid>

        <Divider sx={{ my: 2 }} />
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Typography variant="subtitle2">Items</Typography>
          <Button size="small" startIcon={<AddIcon />} onClick={() => setItems((c) => [...c, { ...BLANK_ITEM }])}>
            Add item
          </Button>
        </Stack>
        <Typography variant="caption" color="text.secondary">
          Item 1, Item 2, and so on. Each offers a list of dishes and says how many the customer picks.
        </Typography>

        <Stack spacing={2} sx={{ mt: 1 }}>
          {items.map((item, index) => {
            const chosen = item.menuItemIds
              .map((id) => known.get(id))
              .filter((dish): dish is Dish => Boolean(dish))
            return (
              <Card key={index} variant="outlined">
                <CardContent>
                  <Grid container spacing={2} alignItems="flex-start">
                    <Grid item xs={12} md={1}>
                      <Chip label={`Item ${index + 1}`} color="primary" size="small" />
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <TextField
                        fullWidth
                        size="small"
                        label="Called"
                        value={item.itemLabel}
                        onChange={(event) => updateItem(index, { itemLabel: event.target.value })}
                        placeholder={`Item ${index + 1}`}
                      />
                    </Grid>
                    <Grid item xs={6} md={3}>
                      <TextField
                        fullWidth
                        size="small"
                        type="number"
                        label="How many chosen"
                        value={item.maxSelections}
                        onChange={(event) => updateItem(index, { maxSelections: event.target.value })}
                        helperText="Exactly this many"
                      />
                    </Grid>
                    <Grid item xs={6} md={2}>
                      <TextField
                        fullWidth
                        size="small"
                        type="number"
                        label="Extra item price"
                        value={item.extraItemPrice}
                        onChange={(event) => updateItem(index, { extraItemPrice: event.target.value })}
                        disabled={!item.allowExtra}
                      />
                    </Grid>
                    <Grid item xs={6} md={1}>
                      <FormControlLabel
                        control={
                          <Switch
                            size="small"
                            checked={item.allowExtra}
                            onChange={(event) => updateItem(index, { allowExtra: event.target.checked })}
                          />
                        }
                        label="Extra"
                      />
                    </Grid>
                    <Grid item xs={12} md={1} sx={{ textAlign: 'right' }}>
                      <IconButton
                        size="small"
                        onClick={() => setItems((c) => c.filter((_, position) => position !== index))}
                        disabled={items.length === 1}
                      >
                        <DeleteOutlineIcon fontSize="small" />
                      </IconButton>
                    </Grid>

                    <Grid item xs={12}>
                      <DishPicker
                        label={`Choices for item ${index + 1}`}
                        chosen={chosen}
                        onChange={(value) => {
                          remember(value)
                          updateItem(index, {
                            menuItemIds: value.map((option) => option.id),
                            mandatoryIds: item.mandatoryIds.filter((id) =>
                              value.some((option) => option.id === id),
                            ),
                            // A dish just added is a whole serving until told
                            // otherwise; one removed takes its portion with it.
                            portions: Object.fromEntries(
                              value.map((option) => [
                                option.id,
                                item.portions[option.id] ?? WHOLE_PORTION,
                              ]),
                            ),
                            remarks: Object.fromEntries(
                              value.map((option) => [option.id, item.remarks[option.id] ?? '']),
                            ),
                          })
                        }}
                      />
                    </Grid>

                    {chosen.length > 0 && (
                      <Grid item xs={12}>
                        <PortionList
                          chosen={chosen}
                          portions={item.portions}
                          remarks={item.remarks}
                          onChange={(portions) => updateItem(index, { portions })}
                          onRemarksChange={(remarks) => updateItem(index, { remarks })}
                        />
                      </Grid>
                    )}

                    {chosen.length > 0 && (
                      <Grid item xs={12}>
                        <Autocomplete
                          multiple
                          size="small"
                          options={chosen}
                          value={chosen.filter((dish) => item.mandatoryIds.includes(dish.id))}
                          getOptionLabel={(option) => option.name}
                          onChange={(_event, value) =>
                            updateItem(index, { mandatoryIds: value.map((option) => option.id) })
                          }
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              label="Always included (not optional)"
                              placeholder="Optional"
                            />
                          )}
                        />
                      </Grid>
                    )}
                  </Grid>
                </CardContent>
              </Card>
            )
          })}
        </Stack>


        {basePricePerPerson && (
          <Alert severity="success" sx={{ mt: 2 }}>
            {pricing === 'INCLUSIVE'
              ? `${formatMoney(Number(basePricePerPerson))} per head, GST included.`
              : `${formatMoney(Number(basePricePerPerson))} per head, plus ${taxRatePercent || 0}% GST.`}
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={submit} disabled={!valid || busy}>
          Create package
        </Button>
      </DialogActions>
    </Dialog>
  )
}
